import { createRef, forwardRef, useEffect } from "react";

import { format } from "date-fns";
import sv from "date-fns/locale/sv";
import DatePicker, {
  CalendarContainer,
  registerLocale,
  setDefaultLocale,
} from "react-datepicker";

import CalendarIcon from "@/assets/icons/calendar.svg";
import { useUser } from "@/hooks/useUser";
import { cx, ensureIsDate, formatDateForApi } from "@/utils";
import Button from "@components/Button";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "@components/Dropdown";

import styles from "./AEDatePicker.module.css";

registerLocale("sv", sv);

const ref = createRef();

const PresetButton = (props) => (
  <Button size="sm" variant="secondary" {...props}>
    {props.children}
  </Button>
);

// https://github.com/Hacker0x01/react-datepicker/issues/862#issuecomment-534736357
const DatePickerButton = forwardRef(({ onClick, label }, ref) => (
  <Button
    className={styles.button}
    variant="outline-gray"
    size="sm"
    onClick={onClick}
    ref={ref}
    icon={CalendarIcon}
  >
    {label}
  </Button>
));

DatePickerButton.displayName = "DatePickerButton";

export const AEDatePicker = (props) => {
  const { data } = useUser();
  const language = data?.data?.language;

  useEffect(() => {
    if (language) setDefaultLocale(language);
  }, [language]);

  const Container = ({ className, children }) => (
    <CalendarContainer className={className}>
      <div className="react-datepicker__triangle"></div>
      <div className={styles.buttonGroup}>
        <PresetButton onClick={() => props.setDatePreset(7, "days")}>
          {L.REPORTS_DATEPICKER_RANGE_7_DAYS}
        </PresetButton>
        <PresetButton onClick={() => props.setDatePreset(30, "days")}>
          {L.REPORTS_DATEPICKER_RANGE_30_DAYS}
        </PresetButton>
        <PresetButton onClick={() => props.setDatePreset(3, "months")}>
          {L.REPORTS_DATEPICKER_RANGE_3_MONTHS}
        </PresetButton>

        <Dropdown>
          <DropdownToggle asChild>
            <Button variant="secondary-grayish" size="sm">
              »
            </Button>
          </DropdownToggle>
          <DropdownMenu withPortal={false}>
            <DropdownItem
              className="btn-sm"
              onClick={() => props.setDatePreset(6, "months")}
            >
              {L.REPORTS_DATEPICKER_RANGE_6_MONTHS}
            </DropdownItem>
            <DropdownItem
              className="btn-sm"
              onClick={() => props.setDatePreset(12, "months")}
            >
              {L.REPORTS_DATEPICKER_RANGE_12_MONTHS}
            </DropdownItem>
            <DropdownItem
              className="btn-sm"
              onClick={() => props.setDatePreset("prev_year")}
            >
              {L.REPORTS_DATEPICKER_RANGE_PREV_YEAR}
            </DropdownItem>
            <DropdownItem
              className="btn-sm"
              onClick={() => props.setDatePreset("ytd")}
            >
              {L.REPORTS_DATEPICKER_RANGE_YTD}
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>

      <div className={cx(styles.inner, "datepicker--container-inner")}>
        {children}
      </div>
    </CalendarContainer>
  );

  const label = () => {
    let out = `${formatDateForApi(ensureIsDate(props.startDate))} – `;
    if (props.endDate) out += formatDateForApi(ensureIsDate(props.endDate));
    return out;
  };

  return (
    <DatePicker
      customInput={<DatePickerButton ref={ref} label={label()} />}
      calendarContainer={Container}
      {...props}
    />
  );
};

// DatePicker variant for time comparison reports with custom functionality and styles
const timeComparisonRef = createRef();

const DatePickerInputForTimeComparison = forwardRef(
  ({ onClick, label, ...props }, ref) => (
    <DatePickerButton onClick={onClick} ref={ref} {...props}>
      {label}
    </DatePickerButton>
  )
);

DatePickerInputForTimeComparison.displayName =
  "DatePickerInputForTimeComparison";

export const AEDatePickerForTimeComparison = (props) => {
  let { timePeriod, ...restProps } = props;

  const dateFormats = {
    year: "yyyy",
    quarter: "QQQ, yyyy",
    month: "MMM, yyyy",
    week: "yyyy-MM-dd",
    day: "yyyy-MM-dd",
  };

  return (
    <DatePicker
      {...restProps}
      showQuarterYearPicker={timePeriod === "quarter"}
      showMonthYearPicker={timePeriod === "month"}
      showYearPicker={timePeriod === "year"}
      maxDate={new Date()}
      popperPlacement="bottom-start"
      customInput={
        <DatePickerInputForTimeComparison
          ref={timeComparisonRef}
          label={
            props.selected
              ? format(ensureIsDate(props.selected), dateFormats[timePeriod])
              : props.placeholderText
          }
        />
      }
    />
  );
};
